/**
 * utils/moneyHelpers.ts
 * Money conversion utilities
 *
 * Handles conversion between decimal money (for UI) and integer storage (for database)
 * to avoid JavaScript floating point precision issues. Supports multiple currencies
 * and precision levels.
 *
 * Examples:
 * - USD: $100.25 ↔ 10025 (precision: 2)
 * - Crypto: 0.12345678 BTC ↔ 12345678 (precision: 8)
 * - Futures: 4356.50 ↔ 435650 (precision: 2)
 */

// Import percentage formatting from shared number utilities
import { formatPercentageChange as formatPercentageChangeUtil } from './numberHelpers';

/**
 * Convert decimal money to integer for database storage
 * 
 * @param decimal - The decimal amount (e.g., 100.25)
 * @param precision - Number of decimal places (e.g., 2 for USD)
 * @returns Integer representation (e.g., 10025)
 * 
 * @example
 * toMoneyInteger(100.25, 2) // Returns: 10025
 * toMoneyInteger(0.12345678, 8) // Returns: 12345678
 * toMoneyInteger(4356.5, 2) // Returns: 435650
 */
export function toMoneyInteger(decimal: number, precision: number): number {
  if (!Number.isFinite(decimal)) {
    throw new Error(`Invalid decimal value: ${decimal}`);
  }
  
  if (!Number.isInteger(precision) || precision < 0 || precision > 10) {
    throw new Error(`Invalid precision: ${precision}. Must be integer between 0-10`);
  }
  
  const multiplier = Math.pow(10, precision);
  const result = Math.round(decimal * multiplier);
  
  // Validate the conversion doesn't lose precision due to JavaScript limitations
  if (Math.abs(result / multiplier - decimal) > 1e-10) {
    console.warn(`Potential precision loss converting ${decimal} with precision ${precision}`);
  }
  
  return result;
}

/**
 * Convert integer back to decimal for display
 * 
 * @param integer - The integer amount from database (e.g., 10025)
 * @param precision - Number of decimal places (e.g., 2 for USD)
 * @returns Decimal representation (e.g., 100.25)
 * 
 * @example
 * fromMoneyInteger(10025, 2) // Returns: 100.25
 * fromMoneyInteger(12345678, 8) // Returns: 0.12345678
 * fromMoneyInteger(435650, 2) // Returns: 4356.5
 */
export function fromMoneyInteger(integer: number, precision: number): number {
  if (!Number.isInteger(integer)) {
    throw new Error(`Invalid integer value: ${integer}`);
  }
  
  if (!Number.isInteger(precision) || precision < 0 || precision > 10) {
    throw new Error(`Invalid precision: ${precision}. Must be integer between 0-10`);
  }
  
  const divisor = Math.pow(10, precision);
  return integer / divisor;
}

// =============================================================================
// Integer ↔ Decimal Conversion (Form-Friendly)
// =============================================================================

/**
 * Convert a stored integer amount to the decimal value the user sees.
 *
 * @example
 * toDisplayValue(165, 2)   // 1.65
 * toDisplayValue(10, 4)    // 0.001
 * toDisplayValue(undefined, 2) // undefined
 */
export function toDisplayValue(
  integerAmount: number | undefined,
  precision: number | undefined
): number | undefined {
  if (integerAmount === undefined || integerAmount === null) return undefined;
  const p = precision ?? 2;
  return integerAmount / Math.pow(10, p);
}

/**
 * Convert a user-facing decimal value back to the stored integer.
 * Uses Number.EPSILON guard to reduce JS floating-point artifacts.
 *
 * @example
 * toStorageValue(1.65, 2)  // 165
 * toStorageValue(0.001, 4) // 10
 * toStorageValue(undefined, 2) // undefined
 */
export function toStorageValue(
  displayAmount: number | undefined,
  precision: number | undefined
): number | undefined {
  if (displayAmount === undefined || displayAmount === null) return undefined;
  const p = precision ?? 2;
  const factor = Math.pow(10, p);
  return Math.round((displayAmount + Number.EPSILON) * factor);
}

/**
 * Format money for display with currency symbol and proper decimal places
 * 
 * @param amount - Amount as decimal or integer
 * @param currency - Currency code (USD, EUR, BTC, etc.)
 * @param precision - Decimal places (if amount is integer, this is required)
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted string (e.g., "$100.25", "€95.50", "0.12345678 BTC")
 * 
 * @example
 * formatMoney(100.25, 'USD') // Returns: "$100.25"
 * formatMoney(10025, 'USD', 2) // Returns: "$100.25" (from integer)
 * formatMoney(0.12345678, 'BTC') // Returns: "0.12345678 BTC"
 * formatMoney(9550, 'EUR', 2) // Returns: "€95.50"
 */
export function formatMoney(
  amount: number, 
  currency: string, 
  precision?: number,
  locale: string = 'en-US'
): string {
  let decimalAmount: number;
  
  // If precision is provided, treat amount as integer and convert
  if (precision !== undefined) {
    decimalAmount = fromMoneyInteger(amount, precision);
  } else {
    decimalAmount = amount;
    // Determine precision from decimal places
    const decimals = (decimalAmount.toString().split('.')[1] || '').length;
    precision = decimals;
  }
  
  // Handle different currency formatting
  const currencyUpper = currency.toUpperCase();
  
  try {
    // Use Intl.NumberFormat for standard currencies
    if (['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF'].includes(currencyUpper)) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyUpper,
        minimumFractionDigits: precision,
        maximumFractionDigits: precision
      }).format(decimalAmount);
    }
  } catch (_error) {
    // Fall back to manual formatting if Intl fails
    console.warn(`Failed to format ${currencyUpper} with Intl, using fallback`);
  }
  
  // Manual formatting for crypto and other currencies
  const formattedNumber = decimalAmount.toFixed(precision);
  
  // Add currency symbol or code
  switch (currencyUpper) {
    case 'BTC':
    case 'ETH':
    case 'ADA':
    case 'SOL':
      return `${formattedNumber} ${currencyUpper}`;
    default:
      return `${formattedNumber} ${currencyUpper}`;
  }
}

/**
 * Parse money string input to decimal number
 * Handles various input formats and currency symbols
 * 
 * @param input - String input (e.g., "$100.25", "100.25", "€95.50")
 * @returns Decimal number or null if parsing fails
 * 
 * @example
 * parseMoney("$100.25") // Returns: 100.25
 * parseMoney("100.25") // Returns: 100.25
 * parseMoney("€95.50") // Returns: 95.50
 * parseMoney("0.12345678 BTC") // Returns: 0.12345678
 * parseMoney("invalid") // Returns: null
 */
export function parseMoney(input: string): number | null {
  if (!input || typeof input !== 'string') {
    return null;
  }
  
  // Remove currency symbols and whitespace
  const cleaned = input
    .replace(/[$€£¥₹₿]/g, '') // Remove common currency symbols
    .replace(/[A-Z]{3}/g, '') // Remove 3-letter currency codes (USD, BTC, etc.)
    .replace(/\s+/g, '') // Remove whitespace
    .replace(/,/g, ''); // Remove thousand separators
  
  const parsed = parseFloat(cleaned);
  
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Calculate percentage change between two money values
 * 
 * @param oldValue - Original value (as decimal or integer)
 * @param newValue - New value (as decimal or integer)
 * @param precision - If values are integers, precision for conversion
 * @returns Percentage change as decimal (0.1 = 10% increase)
 * 
 * @example
 * calculatePercentageChange(100, 110) // Returns: 0.1 (10% increase)
 * calculatePercentageChange(10000, 11000, 2) // Returns: 0.1 (from $100 to $110)
 */
export function calculatePercentageChange(
  oldValue: number, 
  newValue: number, 
  precision?: number
): number {
  let oldDecimal = oldValue;
  let newDecimal = newValue;
  
  if (precision !== undefined) {
    oldDecimal = fromMoneyInteger(oldValue, precision);
    newDecimal = fromMoneyInteger(newValue, precision);
  }
  
  if (oldDecimal === 0) {
    return newDecimal === 0 ? 0 : Infinity;
  }
  
  return (newDecimal - oldDecimal) / oldDecimal;
}

/**
 * Format percentage change for display
 * Re-exported from numberHelpers for backward compatibility
 *
 * @deprecated Import `formatPercentageChange` from `@/utils/numberHelpers` instead
 *
 * @param percentageChange - Change as decimal (from calculatePercentageChange)
 * @param decimalPlaces - Number of decimal places to show (default: 2)
 * @returns Formatted string with + or - prefix
 *
 * @example
 * formatPercentageChange(0.1) // Returns: "+10.00%"
 * formatPercentageChange(-0.05) // Returns: "-5.00%"
 * formatPercentageChange(0.1234, 1) // Returns: "+12.3%"
 */
export const formatPercentageChange = formatPercentageChangeUtil;

/**
 * Common precision values for different asset types
 */
export const COMMON_PRECISIONS = {
  // Fiat currencies
  USD: 2,
  EUR: 2,
  GBP: 2,
  JPY: 0, // Yen doesn't use decimal places
  CAD: 2,
  AUD: 2,
  CHF: 2,
  
  // Cryptocurrencies
  BTC: 8,
  ETH: 8,
  ADA: 6,
  SOL: 6,
  DOGE: 8,
  
  // Futures/Commodities (examples)
  ES: 2,   // S&P 500 E-mini
  NQ: 2,   // Nasdaq 100 E-mini
  CL: 2,   // Crude Oil
  GC: 1,   // Gold
  
  // Default
  DEFAULT: 2
} as const;

/**
 * Get precision for a currency/asset
 * 
 * @param currency - Currency or asset code
 * @returns Precision value (default: 2)
 */
export function getPrecisionForCurrency(currency: string): number {
  const upperCurrency = currency.toUpperCase() as keyof typeof COMMON_PRECISIONS;
  return COMMON_PRECISIONS[upperCurrency] ?? COMMON_PRECISIONS.DEFAULT;
}